import React, {useEffect, useMemo} from "react";
import {observer} from "mobx-react-lite";
import {Row} from "antd";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {
    CityFilter,
    CompanyFilter,
    SubCompanyFilter,
    PoolFilter
} from "components/_defaultComponents/FilterComponents";
import {ZoneManagementSelect} from "components/_defaultComponents/ZoneManagementSelect";
import {ZoneSelect} from "components/_defaultComponents/ZoneSelect";
import {FilterCol, FilterItem, FilterItemLabel} from "../LiveOperationPage.styles";

// Bölge filtreleri diğerlerinden bir birim geniş: 6 filtrede her sütun 24 birimlik grid'in
// 4 birimi, 5/4 = 1.25.
const ZONE_FILTER_GROW = 1.25;

export const LiveOperationFilters = observer(() => {
    const stores = useStore();
    const filters = stores.meStore.liveOperation.filters;
    const authCities = stores.meStore.auth?.cities;

    const showZoneManagementFilter = stores.meStore.checkRole(
        "liveOperation.showZoneManagementFilter"
    );
    const showZoneFilter = stores.meStore.checkRole("liveOperation.showZoneFilter");

    // Filtre değerleri localStorage'da kalıcı. Rol değişimi ya da paylaşılan tarayıcı sonrası
    // kullanıcı, ekranda görünmeyen bir filtreyle daraltılmış veriye bakmasın diye gizli
    // filtrelerin değeri temizlenir.
    useEffect(() => {
        if (!showZoneManagementFilter && filters.zoneManagementId) {
            stores.meStore.liveOperation.filters.zoneManagementId = undefined;
        }
        if (!showZoneFilter && filters.zoneId) {
            stores.meStore.liveOperation.filters.zoneId = undefined;
        }
        // Şube/havuz filtrelerinde "Tümü" seçeneği kaldırıldı (boş seçim zaten tümü demek);
        // localStorage'dan gelen eski "all" değeri seçenek listesinde karşılığı olmadığı için
        // etikette ham "all" olarak görünürdü.
        if (filters.subCompany?.includes("all")) {
            stores.meStore.liveOperation.filters.subCompany = [];
        }
        if (filters.pool?.includes("all")) {
            stores.meStore.liveOperation.filters.pool = [];
        }
    }, [
        showZoneManagementFilter,
        showZoneFilter,
        filters.zoneManagementId,
        filters.zoneId,
        JSON.stringify(filters.subCompany),
        JSON.stringify(filters.pool),
        stores.meStore.liveOperation.filters
    ]);

    // Bar rolüne göre 4-6 filtre taşıyor; sabit span'de 5+ filtre ikinci satıra taşar ve sayfa
    // viewport'a oturmaz (tablolar + harita yüksekliği bara göre hesaplanıyor).
    const visibleFilterCount = 4 + (showZoneManagementFilter ? 1 : 0) + (showZoneFilter ? 1 : 0);
    // Bölge filtresi görünmeyen kullanıcıda bar eskisi gibi md'de tek satır (4 sütun) kalmalı.
    const filterSpanMd = visibleFilterCount <= 4 ? 6 : 8;

    // "all" (Tümü) geçerli bir seçim: backend yetkili şehirlerle kesiştirdiği için burada tek
    // şehre çekilmemeli — aksi hâlde kullanıcı Tümü'yü seçer seçmez ilk şehre geri düşer.
    useEffect(() => {
        if (
            authCities?.length > 0 &&
            filters.city !== "all" &&
            !authCities.includes(filters.city)
        ) {
            stores.meStore.liveOperation.filters.city = authCities[0];
        }
    }, [authCities, filters.city, stores.meStore.liveOperation.filters]);

    const [, subCompaniesInCity] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyMapList.bind(null, {
            city: filters.city,
            disabled: "false"
        }),
        dependencies: [filters.city],
        condition: Boolean(filters.city) && filters.city !== "all"
    });

    const companyIdsWithBranchInCity = useMemo(
        () => new Set((subCompaniesInCity || []).map(sc => sc.companyId)),
        [subCompaniesInCity]
    );

    const companyCityFilter = (company, city) => {
        if (!city || city === "all") return true;
        return companyIdsWithBranchInCity.has(company?._id);
    };

    return (
        <Row gutter={12} align="middle" wrap>
            <FilterCol xs={24} sm={12} md={filterSpanMd}>
                <FilterItem>
                    <FilterItemLabel>Şehir</FilterItemLabel>
                    <CityFilter
                        showLabel={false}
                        showAllOption
                        forceShowAllOption
                        allowClear
                        placeholder="Şehir Seçin"
                        value={filters.city}
                        onChange={value => {
                            // Temizleme (x) "Tümü" ile aynı anlama gelir. Alanın boş kalması
                            // harita ve şube isteklerini tümden bloke ederdi
                            // (condition: Boolean(filters.city)).
                            stores.meStore.liveOperation.filters.city = value || "all";
                            stores.meStore.liveOperation.filters.company = "";
                            stores.meStore.liveOperation.filters.subCompany = [];
                            stores.meStore.liveOperation.filters.pool = [];
                        }}
                    />
                </FilterItem>
            </FilterCol>
            <FilterCol xs={24} sm={12} md={filterSpanMd}>
                <FilterItem>
                    <FilterItemLabel>Firma</FilterItemLabel>
                    <CompanyFilter
                        hideTitle
                        showAllOption
                        allKey=""
                        placeholder="Tüm Firmalar"
                        city={filters.city}
                        cityFilter={companyCityFilter}
                        value={filters.company}
                        onChange={value => {
                            stores.meStore.liveOperation.filters.company = value;
                            stores.meStore.liveOperation.filters.subCompany = [];
                        }}
                    />
                </FilterItem>
            </FilterCol>
            <FilterCol xs={24} sm={12} md={filterSpanMd}>
                <FilterItem>
                    <FilterItemLabel>Şube</FilterItemLabel>
                    <SubCompanyFilter
                        hideTitle
                        mode="multiple"
                        showAllOption={false}
                        allowClear
                        showSearch
                        disabled={false}
                        placeholder="Tüm Şubeler"
                        company={filters.company}
                        city={filters.city}
                        value={filters.subCompany}
                        onChange={value => {
                            stores.meStore.liveOperation.filters.subCompany = value;
                        }}
                    />
                </FilterItem>
            </FilterCol>
            <FilterCol xs={24} sm={12} md={filterSpanMd}>
                <FilterItem>
                    <FilterItemLabel>Havuz</FilterItemLabel>
                    <PoolFilter
                        showLabel={false}
                        disabled={false}
                        mode="multiple"
                        showAllOption={false}
                        placeholder="Tüm Havuzlar"
                        city={filters.city}
                        optionFilter={pool => (pool.subCompanies?.length || 0) !== 1}
                        value={filters.pool}
                        onChange={value => {
                            stores.meStore.liveOperation.filters.pool = value;
                        }}
                    />
                </FilterItem>
            </FilterCol>
            {showZoneManagementFilter && (
                <FilterCol xs={24} sm={12} md={filterSpanMd} $grow={ZONE_FILTER_GROW}>
                    <FilterItem>
                        <FilterItemLabel>Bölge Müdürlüğü</FilterItemLabel>
                        <ZoneManagementSelect
                            showTitle={false}
                            marginBottom="0"
                            allowClear
                            placeholder="Tüm Bölge Müdürlükleri"
                            value={filters.zoneManagementId}
                            onChange={value => {
                                stores.meStore.liveOperation.filters.zoneManagementId = value;
                                stores.meStore.liveOperation.filters.zoneId = undefined;
                            }}
                        />
                    </FilterItem>
                </FilterCol>
            )}
            {showZoneFilter && (
                <FilterCol xs={24} sm={12} md={filterSpanMd} $grow={ZONE_FILTER_GROW}>
                    <FilterItem>
                        <FilterItemLabel>Bölge</FilterItemLabel>
                        <ZoneSelect
                            showTitle={false}
                            marginBottom="0"
                            allowClear
                            showSyUser={false}
                            placeholder="Tüm Bölgeler"
                            zoneManagementId={filters.zoneManagementId}
                            value={filters.zoneId}
                            onChange={value => {
                                stores.meStore.liveOperation.filters.zoneId = value;
                            }}
                        />
                    </FilterItem>
                </FilterCol>
            )}
        </Row>
    );
});
